import { sendAuthenticated } from 'app/utils/hyrox-mirror/authenticated';
import { type GraphQLResponse, postGraphQL } from 'app/utils/hyrox-mirror/graphql';
import type { MirrorAccess } from 'app/utils/hyrox-mirror/session';
import type { HyroxInstanceMatch, MirrorLookups } from 'app/utils/hyrox-mirror/translate';

// Plain strings on purpose: these run against hyrox-api (or read a single field back from one-fiit) and
// must stay out of codegen, which validates every .gql document against the one-fiit schema.
const HYROX_USER_GYM = 'query HyroxMirrorUserGym($id: Int!) { userById(id: $id) { gym { id } } }';
// One constant per schema even though the selection is currently identical: these run against two
// different APIs, and a divergence on either side has to be fixable without touching the other's query.
const HYROX_EQUIPMENT_NAMES = 'query HyroxMirrorEquipment { allEquipment { id name } }';
const ONE_FIIT_EQUIPMENT_NAMES = 'query HyroxMirrorEquipment { allEquipment { id name } }';
const ONE_FIIT_INSTANCE_PLAN =
  'query HyroxMirrorInstancePlan($id: ProgressivePlanInstanceId!) { progressivePlanInstanceById(id: $id) { progressivePlan { id } } }';
const HYROX_ACTIVE_INSTANCES =
  'query HyroxMirrorActiveInstances { allProgressivePlanInstances(filter: { status: ACTIVE }) { planInstances { id progressivePlan { id } } } }';

interface UserGymData {
  userById: { gym: { id: number } | null } | null;
}

interface EquipmentData {
  allEquipment: Array<{ id: number; name: string }> | null;
}

interface InstancePlanData {
  progressivePlanInstanceById: { progressivePlan: { id: string } | null } | null;
}

interface ActiveInstancesData {
  allProgressivePlanInstances: { planInstances: Array<{ id: string; progressivePlan: { id: string } }> } | null;
}

interface CreateMirrorLookupsArgs {
  hyroxGraphqlUrl: string;
  oneFiitGraphqlUrl: string;
  getHyroxAccessToken: () => Promise<MirrorAccess>;
  refreshHyroxAccessToken: () => Promise<string | null>;
  getOneFiitAuthorization: () => string | null;
}

const dataOrThrow = <TData>(api: string, { data, errors }: GraphQLResponse<TData>): TData | null => {
  if (errors?.length) {
    throw new Error(`hyrox-mirror lookup failed on ${api}: ${errors.map(({ message }) => message).join('; ')}`);
  }

  return data ?? null;
};

export const createMirrorLookups = ({
  hyroxGraphqlUrl,
  oneFiitGraphqlUrl,
  getHyroxAccessToken,
  refreshHyroxAccessToken,
  getOneFiitAuthorization,
}: CreateMirrorLookupsArgs): MirrorLookups => {
  const cache: {
    gymIdByUser: Map<number, Promise<number | null>>;
    hyroxEquipment: Promise<Map<string, number>> | null;
    oneFiitEquipment: Promise<Map<number, string>> | null;
  } = { gymIdByUser: new Map(), hyroxEquipment: null, oneFiitEquipment: null };

  // Same refresh-once-and-retry as the replay itself, so a stale token fails the same way in both places
  const queryHyrox = async <TData>(query: string, variables: Record<string, unknown> = {}): Promise<TData | null> => {
    const sent = await sendAuthenticated<TData>({
      getAccessToken: getHyroxAccessToken,
      refreshAccessToken: refreshHyroxAccessToken,
      request: { query, url: hyroxGraphqlUrl, variables },
    });

    if (!sent.ok) {
      throw new Error(`hyrox-mirror lookup without a hyrox session (${sent.reason})`);
    }

    return dataOrThrow('hyrox-api', sent.response);
  };

  const queryOneFiit = async <TData>(query: string, variables: Record<string, unknown>): Promise<TData | null> => {
    const authorization = getOneFiitAuthorization();

    if (authorization === null) {
      throw new Error('hyrox-mirror lookup without a one-fiit session');
    }

    return dataOrThrow(
      'one-fiit-api',
      await postGraphQL<TData>({ authorization, query, url: oneFiitGraphqlUrl, variables }),
    );
  };

  const getHyroxGymId = (hyroxUserId: number): Promise<number | null> => {
    const cached = cache.gymIdByUser.get(hyroxUserId);

    if (cached) {
      return cached;
    }

    const pending = queryHyrox<UserGymData>(HYROX_USER_GYM, { id: hyroxUserId })
      .then((data) => data?.userById?.gym?.id ?? null)
      .catch((error: unknown) => {
        cache.gymIdByUser.delete(hyroxUserId);
        throw error;
      });

    cache.gymIdByUser.set(hyroxUserId, pending);

    return pending;
  };

  const getHyroxEquipmentIdByName = (): Promise<Map<string, number>> => {
    if (cache.hyroxEquipment === null) {
      cache.hyroxEquipment = queryHyrox<EquipmentData>(HYROX_EQUIPMENT_NAMES)
        .then((data) => new Map((data?.allEquipment ?? []).map(({ id, name }) => [name, id])))
        .catch((error: unknown) => {
          cache.hyroxEquipment = null;
          throw error;
        });
    }

    return cache.hyroxEquipment;
  };

  const getOneFiitEquipmentNameById = (): Promise<Map<number, string>> => {
    if (cache.oneFiitEquipment === null) {
      cache.oneFiitEquipment = queryOneFiit<EquipmentData>(ONE_FIIT_EQUIPMENT_NAMES, {})
        .then((data) => new Map((data?.allEquipment ?? []).map(({ id, name }) => [id, name])))
        .catch((error: unknown) => {
          cache.oneFiitEquipment = null;
          throw error;
        });
    }

    return cache.oneFiitEquipment;
  };

  const getOneFiitInstancePlanId = async (instanceId: string): Promise<string | null> => {
    const data = await queryOneFiit<InstancePlanData>(ONE_FIIT_INSTANCE_PLAN, { id: instanceId });

    return data?.progressivePlanInstanceById?.progressivePlan?.id ?? null;
  };

  // Cancelling is destructive and unverifiable from here, so an ambiguous match is refused rather than
  // guessed. hyrox-api is expected to hold at most one ACTIVE instance per (gym, plan), but that is not
  // enforced in this repo and a failed StartProgressivePlan replay can leave a pre-cutover instance beside
  // a post-cutover one. Taking the first of several would cancel the wrong plan and report success.
  const getHyroxActiveInstanceIdForPlan = async (planId: string): Promise<HyroxInstanceMatch> => {
    const data = await queryHyrox<ActiveInstancesData>(HYROX_ACTIVE_INSTANCES);
    const matches = (data?.allProgressivePlanInstances?.planInstances ?? []).filter(
      ({ progressivePlan }) => progressivePlan.id === planId,
    );

    if (matches.length === 0) {
      return { found: false, reason: 'no_active_hyrox_instance' };
    }

    if (matches.length > 1) {
      return { found: false, reason: 'ambiguous_hyrox_instance' };
    }

    return { found: true, id: matches[0].id };
  };

  return {
    getHyroxActiveInstanceIdForPlan,
    getHyroxEquipmentIdByName,
    getHyroxGymId,
    getOneFiitEquipmentNameById,
    getOneFiitInstancePlanId,
  };
};
